<template>
	<div class="chart-controls flex relative lg:translate-y-[-60px] mb-0">
		<!-- Interval toggle for 1D and 5D ranges -->
		<div
			v-if="showIntervalToggle"
			class="interval-controls"
		>
			<button
				v-for="intervalOption in intervalOptions"
				:key="intervalOption"
				class="interval-btn"
				:class="{ active: currentInterval === intervalOption }"
				:title="`${intervalOption} interval`"
				@click="updateInterval(intervalOption)"
			>
				{{ intervalOption }}
			</button>
		</div>

		<div class="zoom-controls relative">
			<button
				class="zoom-btn zoom-out-btn"
				title="Zoom out"
				@click="debouncedZoomOut"
			>
				−
			</button>
			<button
				class="zoom-btn zoom-in-btn"
				title="Zoom in"
				@click="debouncedZoomIn"
			>
				+
			</button>
		</div>
	</div>
	<div class="highcharts-stock-chart">
		<div class="chart-wrapper">
			<div
				ref="chartContainer"
				class="chart-container"
				:style="{ height: height + 'px' }"
			></div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, watch, nextTick, computed } from "vue";
import Highcharts from "highcharts/highstock";
import type {
	Options,
	SeriesCandlestickOptions,
	SeriesColumnOptions,
	SeriesAreaOptions,
	StockChart
} from "highcharts";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

// Extend dayjs with timezone support
dayjs.extend(utc);
dayjs.extend(timezone);

// Default timezone to New York (used for parsing/local wall time)
const NY_TZ = "America/New_York";
dayjs.tz.setDefault(NY_TZ);

// Type definitions
interface StockDataPoint {
	date?: string;
	time?: string;
	open: number;
	high: number;
	low: number;
	close: number;
	volume?: number;
	sharevolume?: number;
}

const props = defineProps({
	data: {
		type: Array,
		required: true
	},
	chartType: {
		type: String,
		default: "Line Chart",
		validator: (value: string) =>
			["area", "candles", "Line Chart", "Candlestick"].includes(value)
	},
	selectedRange: {
		type: String,
		default: "1D"
	},
	// Spacing between candlesticks and volume bars
	// 0.0 = no spacing (bars touch)
	// 0.1 = 10% spacing (default)
	// 2.0 = maximum spacing (very thin bars)
	barSpacing: {
		type: Number,
		default: 0.2,
		validator: (value: number) => value >= 0 && value <= 2.0
	},
	// Chart interval for 1D and 5D ranges
	interval: {
		type: String,
		default: "5m",
		validator: (value: string) => ["5m", "15m", "30m", "1h"].includes(value)
	},
	// Chart height - if not provided, defaults to 420
	height: {
		type: Number,
		default: 420
	},
	isEmbedded: {
		type: Boolean,
		default: false
	}
});

const { env } = useRuntimeConfig().public;

const chartContainer = ref<HTMLElement | null>(null);
let chart: StockChart | null = null;

const chartTransitionTimeout = ref<NodeJS.Timeout | null>(null);

// Mobile detection for hiding plot bands on 5D mobile view
const isMobile = computed(
	() => window.matchMedia("(max-width: 767px)").matches
);

// Interval management
const intervalOptions = ["5m", "15m", "30m", "1h"];
const $router = useRoute();
const router = useRouter();
const showIntervalToggle = computed(() => {
	return ["1D", "5D"].includes(props.selectedRange);
});

const currentInterval =
	env === "dev"
		? ref(($router.query.interval as string) || props.interval)
		: ref(props.interval);

// Track zoom state for tick configuration
const isZoomed = ref(false);

// Track zoom level for date formatting (0 = not zoomed, 1+ = zoomed)
const zoomLevel = ref(0);

// Track initial zoom range for 1D and 5D charts to prevent over-zooming
const initialZoomRange = ref<number | null>(null);

// Debounce zoom operations to prevent crashes
let zoomTimeout: NodeJS.Timeout | null = null;
const ZOOM_DEBOUNCE_MS = 50; // 50ms debounce for better responsiveness

// Debounce interval changes only for 1D and 5D ranges
let intervalTimeout: NodeJS.Timeout | null = null;

// Chart state management for safe updates
const isChartUpdating = ref(false);
const isInitialMount = ref(true);

// --- Helpers: robust timestamp parsing -> always returns a UTC epoch ms ---
// If value has "Z" or an explicit offset, trust it. Otherwise, interpret as NY local time.
function toEpochMs(value: string | number): number {
	if (typeof value === "number") {
		return value;
	}
	if (!value) {
		return NaN;
	}

	// Detect explicit timezone/offset
	const hasExplicitZone =
		/Z$/i.test(value) ||
		/([+\-]\d{2}:\d{2})$/.test(value) ||
		/([+\-]\d{4})$/.test(value);

	if (hasExplicitZone) {
		return dayjs(value).valueOf(); // preserves given offset
	}

	// Interpret wall time as New York local time
	return dayjs.tz(value, NY_TZ).valueOf();
}

// Convert interval string to minutes for Highcharts
const getIntervalMinutes = (interval: string) => {
	switch (interval) {
		case "5m":
			return 5;
		case "15m":
			return 15;
		case "30m":
			return 30;
		case "1h":
			return 60;
		default:
			return 5;
	}
};

// Get tick configuration based on selected range and zoom state
const getTickConfig = (selectedRange: string, isZoomed: boolean = false) => {
	switch (selectedRange) {
		case "1D":
			return {
				tickAmount: 6,
				minTickInterval: 2 * 60 * 60 * 1000, // 2 hours minimum
				dateTimeLabelFormats: {
					millisecond: "%H:%M:%S.%L",
					second: "%H:%M:%S",
					minute: "%H:%M",
					hour: "%H:%M",
					day: "%b %e",
					week: "%b %e",
					month: "%b %Y",
					year: "%Y"
				}
			};
		case "5D":
			return {
				tickAmount: 12,
				minTickInterval: 12 * 60 * 60 * 1000, // 12 hours minimum
				dateTimeLabelFormats: {
					millisecond: "%H:%M:%S.%L",
					second: "%H:%M:%S",
					minute: "%H:%M",
					hour: "%H:%M", // Ensure proper time formatting
					day: "%b %e", // Show "Sep 9", "Sep 10", etc. for daily markers
					week: "%b %e",
					month: "%b %Y",
					year: "%Y"
				}
			};
		case "1M":
			return {
				tickAmount: 4,
				minTickInterval: 7 * 24 * 60 * 60 * 1000, // 1 week minimum to force 4 labels
				dateTimeLabelFormats: {
					millisecond: "%b %e",
					second: "%b %e",
					minute: "%b %e",
					hour: "%b %e",
					day: "%b %e",
					week: "%b %e",
					month: "%b %Y",
					year: "%Y"
				}
			};
		case "6M":
			return {
				tickAmount: isZoomed ? 4 : 4,
				minTickInterval: isZoomed
					? 2 * 24 * 60 * 60 * 1000
					: 45 * 24 * 60 * 60 * 1000, // 2 days when zoomed, ~1.5 months default
				dateTimeLabelFormats: isZoomed
					? {
							millisecond: "%b %e",
							second: "%b %e",
							minute: "%b %e",
							hour: "%b %e",
							day: "%b %e",
							week: "%b %e",
							month: "%b %Y",
							year: "%Y"
						}
					: {
							millisecond: "%b %Y",
							second: "%b %Y",
							minute: "%b %Y",
							hour: "%b %Y",
							day: "%b %Y",
							week: "%b %Y",
							month: "%b %Y",
							year: "%Y"
						}
			};
		case "1Y":
			return {
				tickAmount: 4,
				minTickInterval: 90 * 24 * 60 * 60 * 1000, // 3 months minimum
				dateTimeLabelFormats: {
					millisecond: "%b %y",
					second: "%b %y",
					minute: "%b %y",
					hour: "%b %y",
					day: "%b %y",
					week: "%b %y",
					month: "%b %y",
					year: "%y"
				}
			};
		case "5Y":
			return {
				tickAmount: 5, // Always limit to 5 labels
				minTickInterval: isZoomed
					? 90 * 24 * 60 * 60 * 1000
					: 365 * 24 * 60 * 60 * 1000, // 3 months when zoomed, 1 year default
				dateTimeLabelFormats: isZoomed
					? {
							millisecond: "%b %Y",
							second: "%b %Y",
							minute: "%b %Y",
							hour: "%b %Y",
							day: "%b %Y",
							week: "%b %Y",
							month: "%b %Y",
							year: "%Y"
						}
					: {
							millisecond: "%Y",
							second: "%Y",
							minute: "%Y",
							hour: "%Y",
							day: "%Y",
							week: "%Y",
							month: "%Y",
							year: "%Y"
						}
			};
		case "All":
			return {
				tickAmount: 5, // Always limit to 5 labels
				minTickInterval: isZoomed
					? 90 * 24 * 60 * 60 * 1000
					: 365 * 24 * 60 * 60 * 1000, // 3 months when zoomed, 1 year default
				dateTimeLabelFormats: isZoomed
					? {
							millisecond: "%b %Y",
							second: "%b %Y",
							minute: "%b %Y",
							hour: "%b %Y",
							day: "%b %Y",
							week: "%b %Y",
							month: "%b %Y",
							year: "%Y"
						}
					: {
							millisecond: "%Y",
							second: "%Y",
							minute: "%Y",
							hour: "%Y",
							day: "%Y",
							week: "%Y",
							month: "%Y",
							year: "%Y"
						}
			};
		default:
			return {
				tickAmount: 6,
				minTickInterval: 2 * 60 * 60 * 1000,
				dateTimeLabelFormats: {
					millisecond: "%H:%M:%S.%L",
					second: "%H:%M:%S",
					minute: "%H:%M",
					hour: "%H:%M",
					day: "%b %e",
					week: "%b %e",
					month: "%b %Y",
					year: "%Y"
				}
			};
	}
};

// Function to aggregate OHLC data for different intervals
const aggregateOHLCData = (data: StockDataPoint[], intervalMinutes: number) => {
	if (intervalMinutes === 5) {
		// No aggregation needed for 5-minute data
		return data;
	}

	const aggregatedData: StockDataPoint[] = [];
	const intervalMs = intervalMinutes * 60 * 1000;

	// Group data by time intervals
	const groups = new Map<number, StockDataPoint[]>();

	data.forEach((point) => {
		const time = toEpochMs(point.time ?? point.date!);
		const intervalStart = Math.floor(time / intervalMs) * intervalMs;

		if (!groups.has(intervalStart)) {
			groups.set(intervalStart, []);
		}
		groups.get(intervalStart)!.push(point);
	});

	// Aggregate each group
	groups.forEach((group, intervalStart) => {
		if (group.length === 0) {
			return;
		}

		// Sort by time to ensure proper OHLC calculation
		group.sort((a, b) => {
			const timeA = toEpochMs(a.time ?? a.date!);
			const timeB = toEpochMs(b.time ?? b.date!);
			return timeA - timeB;
		});

		const first = group[0];
		const last = group[group.length - 1];

		// Calculate OHLC
		const open = first.open;
		const close = last.close;
		const high = Math.max(...group.map((p) => p.high));
		const low = Math.min(...group.map((p) => p.low));
		const volume = group.reduce(
			(sum, p) => sum + (p.volume ?? p.sharevolume ?? 0),
			0
		);

		aggregatedData.push({
			time: new Date(intervalStart).toISOString(),
			open,
			high,
			low,
			close,
			volume,
			sharevolume: volume
		});
	});

	// Sort aggregated data by time
	return aggregatedData.sort((a, b) => {
		const timeA = toEpochMs(a.time ?? a.date!);
		const timeB = toEpochMs(b.time ?? b.date!);
		return timeA - timeB;
	});
};

const numberFormatter = new Intl.NumberFormat("en-US", {
	notation: "compact",
	compactDisplay: "short",
	minimumFractionDigits: 2,
	maximumFractionDigits: 2
});

// Format stock price - 4 decimals for prices below $0.10, otherwise 2 decimals
const formatPrice = (value: number | null | undefined): string => {
	if (value === null || value === undefined || isNaN(value)) {
		return "0.00";
	}

	if (value < 0.1) {
		return value.toFixed(4);
	}
	return value.toFixed(2);
};

// Function to determine volume bar color based on candle direction
const getVolumeBarColor = (dataPoint: StockDataPoint) => {
	const { open, close } = dataPoint;
	if (close > open) {
		return "rgba(65, 147, 22, 0.6)";
	} else if (close < open) {
		return "rgba(209, 81, 60, 0.6)";
	} else {
		return "rgba(65, 147, 22, 0.6)";
	}
};

// Chart configuration
const getChartOptions = (
	minPrice: number,
	_minVolume: number,
	data: StockDataPoint[]
) => {
	const isCandlestick =
		props.chartType === "candles" || props.chartType === "Candlestick";

	const baseOptions: Options = {
		chart: {
			type: isCandlestick ? "candlestick" : "area",
			backgroundColor: "transparent",
			height: props.height || 420,
			style: {
				fontFamily: "Inter, system-ui, sans-serif"
			},
			panning: {
				enabled: true,
				type: "xy" // Enable both X and Y panning for better mobile experience
			},
			// Mobile-specific options
			...(isMobile.value && {
				events: {
					load: function () {
						// Ensure touch events work properly on mobile
						this.container.addEventListener(
							"touchstart",
							function (e) {
								e.preventDefault();
							},
							{ passive: false }
						);

						this.container.addEventListener(
							"touchmove",
							function (e) {
								e.preventDefault();
							},
							{ passive: false }
						);
					}
				}
			})
		},
		time: {
			timezone: NY_TZ
		},
		legend: {
			enabled: false
		},
		credits: {
			enabled: false
		},
		rangeSelector: {
			enabled: false
		},
		navigator: {
			enabled: false
		},
		scrollbar: {
			enabled: false
		},
		tooltip: {
			useHTML: true,
			outside: false, // keep inside chart area
			shape: "rect", // no arrow (callout -> rect)
			borderRadius: 14,
			borderWidth: 0,
			shadow: false,
			padding: 12,
			backgroundColor: "rgba(255,255,255,0.98)",
			className: "hc-card-tooltip", // Multiple shadows via CSS
			followTouchMove: false, // Enable single-finger panning on mobile

			formatter: function (this: any) {
				try {
					const p = this.point;
					const vol = p?.options?.volume ?? p?.volume ?? null;

					return `
        <div style="display:flex;flex-direction:column;gap:6px;">
          <div style="font-weight:600;font-size:24px;line-height:1.1;">
            $${formatPrice(p.y)}
          </div>
          <div style="font-size:14px;opacity:.7;">
            ${formatTooltipDate(p.x, props.selectedRange)}
          </div>
          ${
											vol !== null
												? `<div style="font-size:14px;opacity:.7;">
                 Volume: ${numberFormatter.format(vol)}
               </div>`
												: ""
										}
        </div>
      `;
				} catch (error) {
					console.error("Error in tooltip formatter:", error);
					return `
        <div style="display:flex;flex-direction:column;gap:6px;">
          <div style="font-weight:600;font-size:24px;line-height:1.1;">
            $${formatPrice(this.point.y)}
          </div>
          <div style="font-size:14px;opacity:.7;">
            Invalid Date
          </div>
        </div>
      `;
				}
			},

			// Center the tooltip horizontally on the point and place it below it.
			positioner: function (labelWidth, labelHeight, point) {
				const chart = this.chart;
				const left = chart.plotLeft;
				const top = chart.plotTop;

				const rawX = left + point.plotX - labelWidth / 2;
				const rawY = top + point.plotY + 12; // distance under the marker

				// clamp to plot area
				const minX = left + 4;
				const maxX = left + chart.plotWidth - labelWidth - 4;
				const clampedX = Math.max(minX, Math.min(rawX, maxX));

				const maxY = top + chart.plotHeight - labelHeight - 4;
				const clampedY = Math.min(rawY, maxY);

				return { x: clampedX, y: clampedY };
			}
		},
		xAxis: {
			type: "datetime",
			gridLineColor: "transparent",
			lineColor: "#DFE1E7",
			tickColor: "#DFE1E7",
			labels: {
				style: {
					color: "#818898",
					fontSize: "16px",
					fontWeight: "400"
				},
				rotation: 0,
				align: "center",
				formatter: function (this: any) {
					// For 1D range, show time in 24-hour format
					if (props.selectedRange === "1D") {
						const d = dayjs.tz(this.value, NY_TZ);
						return d.format("HH:mm");
					}

					// For 5D range, show mixed day and 12:00 labels pattern
					if (props.selectedRange === "5D") {
						const d = dayjs.tz(this.value, NY_TZ);
						const hour = d.hour();
						const minute = d.minute();

						// Show date labels at the start of each trading day (4 AM - 10 AM range)
						// This should be around 4 AM (premarket start) or 9:30 AM (market open)
						// Use a broader range to catch the start of trading periods
						const isTradingDayStart = hour >= 4 && hour < 10 && minute < 30;

						if (hour === 0 && minute === 0) {
							return d.format("MMM D"); // "Sep 9", "Sep 10", etc.
						}

						// Show 12:00 labels at midday (12 PM)
						const isMidday = hour === 12 && minute < 30; // 12:00-12:30 PM

						if (isMidday) {
							return "12:00";
						}

						// For all other times, don't show any labels (return empty string)
						return "";
					}

					const d = dayjs.tz(this.value, NY_TZ);

					// For 1Y range, show month + 2-digit year (e.g., "Oct 24", "Jan 25")
					if (props.selectedRange === "1Y") {
						return d.format("MMM YY");
					}

					// For 5Y and All ranges, show months when zoomed 3+ times, years when not zoomed
					if (["5Y", "All"].includes(props.selectedRange)) {
						if (zoomLevel.value >= 3) {
							return d.format("MMM YY"); // Show month + year when zoomed 3+ times (e.g., "Jan 2023", "Feb 2023")
						} else {
							return d.format("YYYY"); // Show just year when not zoomed enough (e.g., "2021", "2022")
						}
					}

					// For other ranges (1M, 6M), show date without year
					return d.format("MMM D");
				}
			},
			...getTickConfig(props.selectedRange, isZoomed.value),
			crosshair: {
				dashStyle: "Dash",
				width: 1,
				color: "#666666",
				zIndex: 5
			},
			plotBands: getTradingHoursPlotBands(data, props.selectedRange),
			// For 1D charts, show extended timeline like Yahoo Finance (NY local day)
			...(props.selectedRange === "1D" &&
				data.length > 0 && {
					min: (() => {
						const firstDataPoint = data[0];
						const base = toEpochMs(firstDataPoint.time ?? firstDataPoint.date!);
						const nyStart = dayjs.tz(base, NY_TZ).startOf("day");
						return nyStart.add(4, "hour").valueOf(); // 4:00 AM NY
					})(),
					max: (() => {
						const firstDataPoint = data[0];
						const base = toEpochMs(firstDataPoint.time ?? firstDataPoint.date!);
						const nyStart = dayjs.tz(base, NY_TZ).startOf("day");
						return nyStart.add(20, "hour").valueOf(); // 8:00 PM NY
					})(),
					ordinal: false, // Disable ordinal axis to show time gaps properly
					type: "datetime" // Ensure datetime axis
				})
		},
		plotOptions: {
			candlestick: {
				color: "#d1513c",
				lineColor: "#d1513c",
				upColor: "#419316",
				upLineColor: "#419316",
				groupPadding: props.barSpacing,
				pointPadding: props.barSpacing
			},
			area: {
				color: "#005bf5",
				lineColor: "#005bf5"
			},
			column: {
				groupPadding: props.barSpacing,
				pointPadding: props.barSpacing,
				borderWidth: 0,
				borderRadius: 0
			},
			series: {
				animation: false,
				// Improve touch experience on mobile
				...(isMobile.value && {
					states: {
						hover: {
							enabled: true
						}
					},
					// Reduce data point density for better touch responsiveness
					dataGrouping: {
						enabled: true,
						groupPixelWidth: 20
					}
				})
			}
		},
		responsive: {
			rules: [
				{
					condition: {
						maxWidth: 768
					},
					chartOptions: {
						xAxis: {
							...getTickConfig(props.selectedRange, isZoomed.value)
						}
					}
				}
			]
		}
	};

	if (isCandlestick) {
		// Candlestick chart configuration
		return {
			...baseOptions,
			yAxis: [
				{
					labels: {
						style: {
							color: "#222222",
							fontSize: "14px",
							fontWeight: "500"
						},
						formatter: function (this: any) {
							return "$" + formatPrice(this.value as number);
						}
					},
					gridLineColor: "#DFE1E7",
					opposite: false,
					min: minPrice,
					height: "70%",
					top: "0%",
					crosshair: {
						dashStyle: "Dash" as const,
						width: 1,
						color: "#666666",
						zIndex: 5
					}
				},
				{
					min: 0,
					labels: {
						style: {
							color: "#818898",
							fontSize: "12px",
							fontWeight: "400"
						},
						formatter: function (this: any) {
							return numberFormatter.format(this.value as number);
						}
					},
					gridLineColor: "#DFE1E7",
					height: "30%",
					top: "70%",
					offset: 0,
					opposite: false
				}
			],
			series: [
				{
					type: "column",
					name: "Volume",
					data: data.map((d) => {
						const volume = d.volume || d.sharevolume;
						return {
							x: toEpochMs(d.date ?? d.time!),
							y: volume,
							color: getVolumeBarColor(d)
						};
					}),
					yAxis: 1,
					zIndex: 1,
					borderWidth: 0,
					enableMouseTracking: false,
					dataGrouping: {
						enabled: false
					},
					states: {
						hover: {
							enabled: false
						},
						inactive: {
							opacity: 1
						}
					}
				} as SeriesColumnOptions,
				{
					type: "candlestick",
					name: "Stock Price",
					yAxis: 0,
					enableMouseTracking: true,
					color: "#d1513c",
					zIndex: 2,
					data: data.map((d) => ({
						x: toEpochMs(d.date ?? d.time!),
						open: d.open,
						high: d.high,
						low: d.low,
						close: d.close,
						volume: d.sharevolume ?? d.volume
					})),
					dataGrouping: {
						enabled: false
					}
				} as SeriesCandlestickOptions
			],
			responsive: {
				rules: [
					{
						condition: {
							maxWidth: 768
						},
						chartOptions: {
							xAxis: {
								...getTickConfig(props.selectedRange, isZoomed.value)
							},
							yAxis: [
								{
									labels: {
										style: {
											fontSize: "12px"
										}
									}
								},
								{
									labels: {
										style: {
											fontSize: "10px"
										}
									}
								}
							]
						}
					}
				]
			}
		} as Options;
	} else {
		// Area chart configuration
		return {
			...baseOptions,
			yAxis: [
				{
					labels: {
						style: {
							color: "#222222",
							fontSize: "14px",
							fontWeight: "500"
						},
						formatter: function (this: any) {
							return "$" + formatPrice(this.value as number);
						}
					},
					gridLineColor: "#DFE1E7",
					opposite: false,
					min: minPrice,
					height: "70%",
					top: "0%",
					crosshair: {
						dashStyle: "Dash" as const,
						width: 1,
						color: "#666666",
						zIndex: 5
					}
				},
				{
					min: 0,
					labels: {
						style: {
							color: "#818898",
							fontSize: "12px",
							fontWeight: "400"
						},
						formatter: function (this: any) {
							return numberFormatter.format(this.value as number);
						}
					},
					gridLineColor: "#DFE1E7",
					height: "30%",
					top: "70%",
					offset: 0,
					opposite: false
				}
			],
			series: [
				{
					type: "column",
					name: "Volume",
					data: data.map((d) => {
						const volume = d.volume || d.sharevolume;
						return {
							x: toEpochMs(d.date ?? d.time!),
							y: volume,
							color: getVolumeBarColor(d)
						};
					}),
					yAxis: 1,
					zIndex: 1,
					borderWidth: 0,
					enableMouseTracking: false,
					dataGrouping: {
						enabled: false
					},
					states: {
						hover: {
							enabled: false
						},
						inactive: {
							opacity: 1
						}
					}
				} as SeriesColumnOptions,
				{
					type: "area",
					name: "Stock Price",
					data: data.map((d) => ({
						x: toEpochMs(d.date ?? d.time!),
						y: d.close,
						volume: d.sharevolume ?? d.volume
					})),
					dataGrouping: {
						enabled: false
					},
					fillColor: {
						linearGradient: {
							x1: 0,
							y1: 0,
							x2: 0,
							y2: 1
						},
						stops: [
							[0, "rgba(59, 130, 246, 0.15)"],
							[1, "rgba(59, 130, 246, 0)"]
						]
					},
					yAxis: 0,
					zIndex: 2,
					lineWidth: 2,
					marker: {
						enabled: false
					},
					connectNulls: false // Allow gaps where no data exists
				} as SeriesAreaOptions
			],
			responsive: {
				rules: [
					{
						condition: {
							maxWidth: 768
						},
						chartOptions: {
							xAxis: {
								...getTickConfig(props.selectedRange, isZoomed.value)
							},
							yAxis: [
								{
									labels: {
										style: {
											fontSize: "12px"
										}
									}
								},
								{
									labels: {
										style: {
											fontSize: "10px"
										}
									}
								}
							]
						}
					}
				]
			}
		};
	}
};

// Initialize chart
const initChart = async (data: StockDataPoint[]) => {
	if (!chartContainer.value) {
		return;
	}

	// Validate data before proceeding
	if (!data || data.length === 0) {
		console.warn("initChart called with empty data");
		return;
	}

	await nextTick();

	// Aggregate data based on current interval for 1D and 5D ranges
	let processedData = data;
	if (["1D", "5D"].includes(props.selectedRange)) {
		const intervalMinutes = getIntervalMinutes(currentInterval.value);
		processedData = aggregateOHLCData(data, intervalMinutes);
	}

	// Validate processed data
	if (!processedData || processedData.length === 0) {
		console.warn("initChart: processed data is empty");
		return;
	}

	const lowestPrice = Math.min(...processedData.map((d) => d.close));
	const lowestVolume = Math.min(
		...processedData.map((d) => d.sharevolume ?? d.volume ?? 0)
	);
	const options = getChartOptions(lowestPrice, lowestVolume, processedData);

	try {
		chart = Highcharts.stockChart(chartContainer.value, options);

		// Set initial zoom range for 1D and 5D charts
		if (["1D", "5D"].includes(props.selectedRange) && processedData.length > 0) {
			const dataTimes = processedData.map((d) => toEpochMs(d.time ?? d.date!));
			const minDataTime = Math.min(...dataTimes);
			const maxDataTime = Math.max(...dataTimes);
			initialZoomRange.value = maxDataTime - minDataTime;
		}
	} catch (error) {
		console.error("Error initializing chart:", error);
	} finally {
		isChartUpdating.value = false;
	}
};

// Update chart data with smooth transition
const updateChart = (data: StockDataPoint[]) => {
	// Prevent multiple simultaneous updates
	if (isChartUpdating.value) {
		return;
	}

	// Clear any existing transition timeout
	if (chartTransitionTimeout.value) {
		clearTimeout(chartTransitionTimeout.value);
		chartTransitionTimeout.value = null;
	}

	isChartUpdating.value = true;

	// Delay destroy+init together so the old chart stays visible until the new one is ready
	chartTransitionTimeout.value = setTimeout(() => {
		if (chart) {
			try {
				chart.destroy();
			} catch (error) {
				console.warn("Error destroying chart:", error);
			}
			chart = null;
		}
		initChart(data);
		chartTransitionTimeout.value = null;
	}, 50);
};

function trimOneDayData(data: StockDataPoint[]): StockDataPoint[] {
	if (props.selectedRange !== "1D") {
		return data;
	}

	if (!data.length) {
		return data;
	}

	// Find the most recent date instead of using first element
	const mostRecentDataPoint = data.reduce((latest, current) => {
		const currentTime = toEpochMs(current.time || current.date!);
		const latestTime = toEpochMs(latest.time || latest.date!);
		return currentTime > latestTime ? current : latest;
	});

	const referenceTs = toEpochMs(
		mostRecentDataPoint.time || mostRecentDataPoint.date!
	);
	const refDay = dayjs.tz(referenceTs, NY_TZ);

	return data
		.filter((d) => {
			const ts = toEpochMs(d.time || d.date!);
			return dayjs.tz(ts, NY_TZ).isSame(refDay, "day");
		})
		.sort((a, b) => toEpochMs(a.time || a.date!) - toEpochMs(b.time || b.date!));
}

function formatTooltipDate(epochMs: number, range: string) {
	// Simple dayjs conversion
	const d = dayjs.tz(epochMs, NY_TZ);

	if (!d.isValid()) {
		return "Invalid Date";
	}

	// For 1D and 5D ranges, show time with current date
	if (range === "1D" || range === "5D") {
		return d.format("MMM D, YYYY h:mm A");
	}

	// For other ranges, show just the date
	return d.format("MMM D, YYYY");
}

// Function to generate premarket and aftermarket hour plot bands (NY local)
function getTradingHoursPlotBands(
	data: StockDataPoint[],
	selectedRange: string
) {
	if (!["1D", "5D"].includes(selectedRange) || !data.length) {
		return [];
	}

	// Hide plot bands for 5D on mobile
	if (selectedRange === "5D" && isMobile.value) {
		return [];
	}

	const plotBands: any[] = [];
	const seenDays = new Set<string>();

	// Build unique NY-local days present in the data
	data.forEach((d) => {
		const ts = toEpochMs(d.date ?? d.time!);
		const dayKey = dayjs.tz(ts, NY_TZ).format("YYYY-MM-DD");
		seenDays.add(dayKey);
	});

	// Create premarket and aftermarket bands for each unique NY-local date
	for (const dayKey of seenDays) {
		const nyDayStart = dayjs.tz(dayKey, NY_TZ).startOf("day");

		// Premarket hours: 4:00 AM - 9:30 AM NY (7% opacity)
		const preStart = nyDayStart.add(4, "hour").valueOf();
		const preEnd = nyDayStart.add(9.5, "hour").valueOf();
		const premarketColor = "rgba(67, 131, 255, 0.25)";

		plotBands.push({
			from: preStart,
			to: preEnd,
			color: premarketColor,
			zIndex: -1,
			className: "premarket-band"
		});

		// Aftermarket hours: 4:00 PM - 8:00 PM NY (20% opacity)
		const aftStart = nyDayStart.add(16, "hour").valueOf();
		const aftEnd = nyDayStart.add(20, "hour").valueOf();
		const aftermarketColor = "rgba(67, 131, 255, 0.15)";

		plotBands.push({
			from: aftStart,
			to: aftEnd,
			color: aftermarketColor,
			zIndex: -1,
			className: "aftermarket-band"
		});
	}

	return plotBands;
}

// Watch for data changes with deep comparison to prevent unnecessary updates
watch(
	() => props.data,
	(newData, oldData) => {
		if (!newData || newData.length === 0) {
			return;
		}

		// On initial mount, if chart doesn't exist yet, initialize it
		// This handles the case where data arrives after component mount
		if (isInitialMount.value && !chart) {
			initChart(trimOneDayData(newData as StockDataPoint[]));
			return;
		}

		// Skip update on initial mount if chart already exists (handled by onMounted)
		if (isInitialMount.value) {
			return;
		}

		// Skip update if data hasn't actually changed (deep comparison)
		if (oldData && newData.length === oldData.length) {
			// Quick check: compare first and last data points
			const newFirst = newData[0] as StockDataPoint | undefined;
			const oldFirst = oldData[0] as StockDataPoint | undefined;
			const newLast = newData[newData.length - 1] as StockDataPoint | undefined;
			const oldLast = oldData[oldData.length - 1] as StockDataPoint | undefined;

			if (
				newFirst &&
				oldFirst &&
				newFirst.close === oldFirst.close &&
				newFirst.time === oldFirst.time &&
				newFirst.date === oldFirst.date &&
				newLast &&
				oldLast &&
				newLast.close === oldLast.close &&
				newLast.time === oldLast.time &&
				newLast.date === oldLast.date
			) {
				// Data appears unchanged, skip update
				return;
			}
		}

		updateChart(trimOneDayData(newData as StockDataPoint[]));
	},
	{ deep: false, immediate: false } // Use shallow watch but do manual comparison
);

// Watch for chart type changes
watch(
	() => props.chartType,
	() => {
		updateChart(trimOneDayData(props.data as StockDataPoint[]));
	}
);

// Watch for bar spacing changes
watch(
	() => props.barSpacing,
	() => {
		updateChart(trimOneDayData(props.data as StockDataPoint[]));
	}
);

// Watch for height changes
watch(
	() => props.height,
	(newHeight) => {
		if (chart && newHeight) {
			chart.setSize(null, newHeight, false);
		}
	}
);

// Watch for interval changes
watch(currentInterval, () => {
	if (["1D", "5D"].includes(props.selectedRange)) {
		updateChart(trimOneDayData(props.data as StockDataPoint[]));
	}
});

// Watch for interval prop changes
watch(
	() => props.interval,
	(newInterval) => {
		currentInterval.value = newInterval;
	}
);

// Watch for selected range changes to reset zoom state
watch(
	() => props.selectedRange,
	() => {
		isZoomed.value = false;
		zoomLevel.value = 0; // Reset zoom level
		initialZoomRange.value = null; // Reset initial zoom range
	}
);

// Watch for mobile breakpoint changes to update plot bands and tick amounts
watch(isMobile, () => {
	if (props.selectedRange === "5D" || props.selectedRange === "1D") {
		updateChart(trimOneDayData(props.data as StockDataPoint[]));
	}
});

// Lifecycle hooks
onMounted(() => {
	// Only initialize if data is available
	if (props.data && props.data.length > 0) {
		initChart(trimOneDayData(props.data as StockDataPoint[]));
	}

	// Mark initial mount as complete after a short delay
	// This allows the watcher to handle data that arrives after mount
	nextTick(() => {
		isInitialMount.value = false;

		// If data wasn't available on mount but is now, initialize the chart
		if (props.data && props.data.length > 0 && !chart) {
			initChart(trimOneDayData(props.data as StockDataPoint[]));
		}
	});
});

onUnmounted(() => {
	if (chart) {
		chart.destroy();
		chart = null;
	}
	if (zoomTimeout) {
		clearTimeout(zoomTimeout);
		zoomTimeout = null;
	}
	if (intervalTimeout) {
		clearTimeout(intervalTimeout);
		intervalTimeout = null;
	}
	if (chartTransitionTimeout.value) {
		clearTimeout(chartTransitionTimeout.value);
		chartTransitionTimeout.value = null;
	}
});

// Debounced zoom functions to prevent crashes
const debouncedZoomIn = () => {
	if (zoomTimeout) {
		clearTimeout(zoomTimeout);
	}
	zoomTimeout = setTimeout(() => {
		zoomIn();
		zoomTimeout = null; // Clear timeout after execution
	}, ZOOM_DEBOUNCE_MS);
};

const debouncedZoomOut = () => {
	if (zoomTimeout) {
		clearTimeout(zoomTimeout);
	}
	zoomTimeout = setTimeout(() => {
		zoomOut();
		zoomTimeout = null; // Clear timeout after execution
	}, ZOOM_DEBOUNCE_MS);
};

// Zoom control methods
const zoomIn = () => {
	if (!chart || !chartContainer.value) {
		return;
	}

	const xAxis = chart.xAxis[0];
	const xExtremes = xAxis.getExtremes();

	// Validate extremes to prevent crashes
	if (
		!xExtremes ||
		xExtremes.min === null ||
		xExtremes.max === null ||
		!isFinite(xExtremes.min) ||
		!isFinite(xExtremes.max)
	) {
		console.warn("Invalid chart extremes, skipping zoom in");
		return;
	}

	const xRange = xExtremes.max - xExtremes.min;

	// Prevent division by zero and invalid ranges
	if (xRange <= 0 || !isFinite(xRange)) {
		console.warn("Invalid chart range, skipping zoom in");
		return;
	}

	// Get the full data range for zoom limits
	const allData = props.data as StockDataPoint[];
	if (allData.length === 0) {
		return;
	}

	const dataTimes = allData
		.map((d) => {
			const time = toEpochMs(d.time ?? d.date!);
			return isFinite(time) ? time : null;
		})
		.filter((time): time is number => time !== null);

	if (dataTimes.length === 0) {
		console.warn("No valid data times found, skipping zoom in");
		return;
	}

	// Define minimum zoom range based on selected range
	let minZoomRange: number;
	switch (props.selectedRange) {
		case "1D":
			minZoomRange = 2 * 60 * 60 * 1000; // 2 hours minimum
			break;
		case "5D":
			minZoomRange = 6 * 60 * 60 * 1000; // 6 hours minimum
			break;
		default:
			minZoomRange = xRange * 0.1; // 10% of current range
	}

	const xCenter = (xExtremes.max + xExtremes.min) / 2;

	// Use appropriate zoom in multiplier based on range
	let zoomMultiplier: number;
	switch (props.selectedRange) {
		case "1D":
		case "5D":
			zoomMultiplier = 0.6; // 40% zoom in per click
			break;
		case "1M":
		case "6M":
		case "1Y":
		case "5Y":
		case "All":
		default:
			zoomMultiplier = 0.8; // 20% zoom in per click
			break;
	}
	const newXRange = xRange * zoomMultiplier;

	// Don't zoom in beyond minimum range
	if (newXRange < minZoomRange) {
		console.warn("Zoom in limit reached for", props.selectedRange);
		return;
	}

	// Calculate new extremes with validation
	const newMin = xCenter - newXRange / 2;
	const newMax = xCenter + newXRange / 2;

	if (!isFinite(newMin) || !isFinite(newMax) || newMin >= newMax) {
		console.warn("Invalid calculated extremes, skipping zoom in");
		return;
	}

	try {
		xAxis.setExtremes(newMin, newMax, true);
	} catch (error) {
		console.error("Error setting chart extremes:", error);
		return;
	}

	// Update zoom state
	isZoomed.value = true;
	zoomLevel.value += 1; // Increment zoom level

	// Force tick update after a short delay to ensure chart is updated
	setTimeout(() => {
		try {
			updateChartTicks();
		} catch (error) {
			console.error("Error updating chart ticks:", error);
		}
	}, 100);
};

const zoomOut = () => {
	if (!chart || !chartContainer.value) {
		return;
	}

	const xAxis = chart.xAxis[0];
	const xExtremes = xAxis.getExtremes();

	// Validate extremes to prevent crashes
	if (
		!xExtremes ||
		xExtremes.min === null ||
		xExtremes.max === null ||
		!isFinite(xExtremes.min) ||
		!isFinite(xExtremes.max)
	) {
		console.warn("Invalid chart extremes, skipping zoom out");
		return;
	}

	const xRange = xExtremes.max - xExtremes.min;

	// Prevent division by zero and invalid ranges
	if (xRange <= 0 || !isFinite(xRange)) {
		console.warn("Invalid chart range, skipping zoom out");
		return;
	}

	const xCenter = (xExtremes.max + xExtremes.min) / 2;

	// Use symmetrical zoom out multiplier based on range
	let zoomMultiplier: number;
	switch (props.selectedRange) {
		case "1D":
		case "5D":
			zoomMultiplier = 1 / 0.6; // 1.67x to match 0.6x zoom in
			break;
		case "1M":
		case "6M":
		case "1Y":
		case "5Y":
		case "All":
		default:
			zoomMultiplier = 1 / 0.8; // 1.25x to match 0.8x zoom in
			break;
	}
	const newXRange = xRange * zoomMultiplier;

	// Get the full data range to prevent zooming beyond available data
	const allData = props.data as StockDataPoint[];
	if (allData.length === 0) {
		return;
	}

	const dataTimes = allData
		.map((d) => {
			const time = toEpochMs(d.time ?? d.date!);
			return isFinite(time) ? time : null;
		})
		.filter((time): time is number => time !== null);

	if (dataTimes.length === 0) {
		console.warn("No valid data times found, skipping zoom out");
		return;
	}

	const minDataTime = Math.min(...dataTimes);
	const maxDataTime = Math.max(...dataTimes);
	const fullDataRange = maxDataTime - minDataTime;

	if (fullDataRange <= 0 || !isFinite(fullDataRange)) {
		console.warn("Invalid data range, skipping zoom out");
		return;
	}

	// Calculate new extremes with bounds checking
	const newMin = xCenter - newXRange / 2;
	const newMax = xCenter + newXRange / 2;

	// Constrain to data range and prevent excessive zoom out
	const constrainedMin = Math.max(newMin, minDataTime);
	const constrainedMax = Math.min(newMax, maxDataTime);

	if (
		!isFinite(constrainedMin) ||
		!isFinite(constrainedMax) ||
		constrainedMin >= constrainedMax
	) {
		console.warn("Invalid calculated extremes, skipping zoom out");
		return;
	}

	// Define maximum zoom out range based on selected range
	let maxAllowedRange: number;
	switch (props.selectedRange) {
		case "1D":
		case "5D":
			// Use initial range as maximum (prevent zooming out beyond default)
			maxAllowedRange = initialZoomRange.value || fullDataRange;
			break;
		case "1M":
		case "6M":
		case "1Y":
		case "5Y":
		case "All":
		default:
			// For longer ranges, allow more zoom out but still limit it
			maxAllowedRange = Math.min(xRange * 1.5, fullDataRange); // Allow up to 1.5x current range
			break;
	}

	// Ensure we don't exceed the full data range
	maxAllowedRange = Math.min(maxAllowedRange, fullDataRange);
	const actualRange = constrainedMax - constrainedMin;

	// For 1D and 5D, check if we're already at or beyond the initial range
	if (["1D", "5D"].includes(props.selectedRange) && initialZoomRange.value) {
		const currentRange = xRange;
		if (currentRange >= initialZoomRange.value) {
			console.warn("Already at maximum zoom out for", props.selectedRange);
			return;
		}
	}

	// Only apply zoom if it's within reasonable bounds and proportional
	if (actualRange <= maxAllowedRange && actualRange > 0) {
		try {
			xAxis.setExtremes(constrainedMin, constrainedMax, true);
		} catch (error) {
			console.error("Error setting chart extremes:", error);
			return;
		}
	} else {
		console.warn("Zoom out limit reached for", props.selectedRange);
	}

	// Update zoom state - more aggressive zoom detection for 5Y range
	const zoomThreshold = props.selectedRange === "5Y" ? 0.6 : 0.8;
	isZoomed.value = actualRange < fullDataRange * zoomThreshold;

	// Decrement zoom level (but don't go below 0)
	if (zoomLevel.value > 0) {
		zoomLevel.value -= 1;
	}

	// Safely update chart ticks
	try {
		updateChartTicks();
	} catch (error) {
		console.error("Error updating chart ticks:", error);
	}
};

// Update chart ticks based on current zoom state
const updateChartTicks = () => {
	if (!chart) {
		return;
	}

	// For 5D range, don't update ticks during zoom as it interferes with the custom formatter
	if (props.selectedRange === "5D") {
		return;
	}

	try {
		const xAxis = chart.xAxis[0];
		if (!xAxis) {
			console.warn("X-axis not found, skipping tick update");
			return;
		}

		const tickConfig = getTickConfig(props.selectedRange, isZoomed.value);

		if (!tickConfig || !tickConfig.tickAmount || !tickConfig.minTickInterval) {
			console.warn("Invalid tick configuration, skipping update");
			return;
		}

		xAxis.update(
			{
				tickAmount: tickConfig.tickAmount,
				minTickInterval: tickConfig.minTickInterval,
				dateTimeLabelFormats: tickConfig.dateTimeLabelFormats
			},
			true
		);

		chart.redraw();
	} catch (error) {
		console.error("Error updating chart ticks:", error);
	}
};

// Interval control methods
const updateInterval = (newInterval: string) => {
	// Prevent rapid switching if chart is updating

	if (env === "dev") {
		router.push({
			query: {
				...$router.query,
				interval: newInterval
			}
		});
	}

	if (isChartUpdating.value) {
		return;
	}

	currentInterval.value = newInterval;
};

// Expose chart instance for external access if needed
defineExpose({
	chart
});
</script>

<style lang="scss" scoped>
@use "~/assets/utils" as *;
.highcharts-stock-chart {
	width: 100%;
	height: 100%;
}

.chart-wrapper {
	position: relative;
	width: 100%;
	height: 100%;
	transition: opacity 0.2s ease-in-out;
}

.chart-container {
	width: 100%;
	height: 420px;
	cursor: grab;
	touch-action: pan-x pan-y; /* Allow both horizontal and vertical panning on touch devices */
	-webkit-touch-callout: none;
	-webkit-user-select: none;
	-khtml-user-select: none;
	-moz-user-select: none;
	-ms-user-select: none;
	user-select: none;
}

.chart-container:active {
	cursor: grabbing;
}

/* Override Highcharts selection cursor */
:deep(.highcharts-container) {
	cursor: grab !important;
}

:deep(.highcharts-container:active) {
	cursor: grabbing !important;
}

/* Indicate horizontal-only panning - only on desktop */
@include breakpoint(lg) {
	:deep(.highcharts-plot-background) {
		cursor: ew-resize !important;
	}

	:deep(.highcharts-plot-background:active) {
		cursor: col-resize !important;
	}
}

.chart-controls {
	position: absolute;
	bottom: 88px;
	left: 0;
	right: 0;
	margin: 0 auto;
	display: flex;
	flex-direction: row;
	gap: 8px;
	z-index: 10;
	background: rgba(236, 239, 243, 1);
	@include breakpoint(lg) {
		top: 8px;
		right: 8px;
		left: inherit;
		bottom: inherit;
	}
}

.interval-controls {
	display: flex;
	gap: 2px;
	background: rgba(255, 255, 255, 0.5);
	border-radius: 6px;
	padding: 0;
	align-items: center;
	@include breakpoint(lg) {
		background: rgba(255, 255, 255, 0.95);
	}
}

.interval-btn {
	width: 36px;
	height: 100%;
	border: none;
	background: transparent;
	border-radius: 4px;
	font-size: 12px;
	font-weight: 500;
	cursor: pointer;
	transition: all 0.2s ease;
	display: flex;
	align-items: center;
	justify-content: center;
	@apply text-slate-400;
	&:hover {
		color: #090d18;
	}
}

.interval-btn:hover {
	background: rgba($color: #fff, $alpha: 0.5);
	color: #000;
	@include breakpoint(lg) {
		background: #fff;
	}
}

.interval-btn.active {
	background: #4285f4;
	color: white;
}

.zoom-controls {
	display: flex;
	gap: 6px;
}

.zoom-btn {
	width: 32px;
	height: 32px;
	border-radius: 6px;
	font-size: 20px;
	font-weight: medium;
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	transition: all 0.2s ease;
	@apply text-slate-400;
}

.zoom-btn:hover {
	transform: translateY(-1px);
	background: rgba($color: #fff, $alpha: 0.5);

	@include breakpoint(lg) {
		background: #fff;
	}
	color: #090d18;
}

.zoom-btn:active {
	transform: translateY(0);
	box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.reset-btn {
	font-size: 14px;
	border-color: #6b7280;
	color: #6b7280;
}

.reset-btn:hover {
	border-color: #4b5563;
	color: #4b5563;
	background: #f9fafb;
}

/* Custom Highcharts styles */
:deep(.highcharts-tooltip) {
	font-family: Inter, system-ui, sans-serif;
}

:deep(.highcharts-axis-labels text) {
	font-family: Inter, system-ui, sans-serif;
}

.hc-card-tooltip {
	border-radius: 14px;
	background: rgba(255, 255, 255, 0.98);
	box-shadow: none;
	padding: 12px;
	pointer-events: none;
	/* keeps hover stable */
}

/* Ensure plot bands don't affect layout */
:deep(.highcharts-plot-band) {
	position: absolute !important;
}

:deep(.premarket-band) {
	position: absolute !important;
	z-index: -1 !important;
}

:deep(.aftermarket-band) {
	position: absolute !important;
	z-index: -1 !important;
}

/* Mobile-specific improvements for touch interaction */
@include max-breakpoint(lg) {
	.chart-container {
		/* Improve touch responsiveness */
		-webkit-touch-callout: none;
		-webkit-user-select: none;
		-khtml-user-select: none;
		-moz-user-select: none;
		-ms-user-select: none;
		user-select: none;

		/* Better touch feedback - disabled zoom effect */
		&:active {
			/* transform: scale(0.98); - disabled to prevent zoom on tap */
			/* transition: transform 0.1s ease; - disabled to prevent zoom on tap */
		}
	}

	/* Improve touch targets for mobile */
	:deep(.highcharts-plot-background) {
		cursor: grab !important;
	}

	:deep(.highcharts-plot-background:active) {
		cursor: grabbing !important;
	}
}
</style>
